/**
 * Wave 3 / NEW G6 — entity chips IFF isDiscoverySearchActive (q.trim()>0 OR entity!=='all').
 */

import type { DiscoveryEntity } from '../discovery/discoverySearchParams.js';

export type BrowseEntityChipsProps = {
  readonly entity: DiscoveryEntity;
  readonly onEntityChange: (entity: DiscoveryEntity) => void;
  readonly allLabel: string;
  readonly productLabel: string;
  readonly shopLabel: string;
  readonly tenantLabel: string;
  readonly groupAriaLabel: string;
  readonly className?: string;
};

const CHIPS: readonly DiscoveryEntity[] = ['all', 'product', 'shop', 'tenant'];

const CHIP_BASE = [
  'inline-flex min-h-11 shrink-0 items-center justify-center rounded-full border px-3 text-sm font-medium',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');

function chipClass(active: boolean): string {
  return [
    CHIP_BASE,
    active
      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
      : 'border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-[var(--color-on-surface)] hover:bg-[var(--color-surface-hover)]',
  ].join(' ');
}

export function BrowseEntityChips({
  entity,
  onEntityChange,
  allLabel,
  productLabel,
  shopLabel,
  tenantLabel,
  groupAriaLabel,
  className,
}: BrowseEntityChipsProps): JSX.Element {
  const labels: Record<DiscoveryEntity, string> = {
    all: allLabel,
    product: productLabel,
    shop: shopLabel,
    tenant: tenantLabel,
  };

  return (
    <div
      className={['flex min-w-0 flex-wrap gap-2', className].filter(Boolean).join(' ')}
      role="group"
      aria-label={groupAriaLabel}
      data-testid="customer-browse-entity-chips"
    >
      {CHIPS.map((chip) => {
        const selected = entity === chip;
        return (
          <button
            key={chip}
            type="button"
            className={chipClass(selected)}
            aria-pressed={selected}
            data-testid={`customer-browse-entity-chip-${chip}`}
            onClick={() => {
              onEntityChange(chip);
            }}
          >
            {labels[chip]}
          </button>
        );
      })}
    </div>
  );
}
